import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';

import { ClaimStrength } from '../../interfaces/application-package';
import { CvTemplate } from '../../interfaces/cv';
import { ClientCvTemplate } from '../../interfaces/client-cv-template.interface';
import { ClientCvTemplateService } from '../templates/client-cv-template.service';

@Component({
  selector: 'bst-tailor-intake',
  templateUrl: './tailor-intake.component.html',
  styleUrls: ['./tailor-intake.component.scss'],
  standalone: false
})
export class TailorIntakeComponent implements OnInit, OnDestroy {

  @Input() public userId!: number;
  @Input() public subjectName: string = '';
  @Input() public clientId?: number;
  @Input() public templates: CvTemplate[] = [];
  @Input() public templateId!: string;
  @Input() public locale: string = 'nl';
  @Input() public vacancyId: number | null = null;
  @Input() public templateType: string = 'built_in';
  @Input() public clientCvTemplateId: number | null = null;

  @Input() public claimStrength: ClaimStrength = 'soft';
  @Input() public availabilityValue: string = '';
  @Input() public salutationValue: string = '';

  @Input() public mode: 'create' | 'settings' = 'create';
  /** Parent-driven in-flight flag (settings mode): blocks a second Regenerate. */
  @Input() public busy = false;
  /** Set once the consultant's edit window is closed; blocks Regenerate. */
  @Input() public disabled = false;
  @Output() public cancelled = new EventEmitter<void>();
  @Output() public regenerate = new EventEmitter<{
    template_id: string;
    locale: string;
    template_type?: string;
    client_cv_template_id?: number | null;
    claim_strength: ClaimStrength;
    availability?: string;
    salutation?: string;
  }>();
  /** Settings mode only: picking a template applies it instantly (no agent). #723 */
  @Output() public templateChanged = new EventEmitter<{
    template_id: string;
    template_type: string;
    client_cv_template_id: number | null;
  }>();

  public form!: FormGroup;
  public submitting = false;
  public brandedTemplates: ClientCvTemplate[] = [];
  public brandedLoading = false;

  public readonly claimOptions: Array<{ value: ClaimStrength; label: string }> = [
    { value: 'soft',       label: 'cv.tailor.intake.claimSoft' },
    { value: 'explicit',   label: 'cv.tailor.intake.claimExplicit' },
    { value: 'collective', label: 'cv.tailor.intake.claimCollective' },
  ];

  private subs: Subscription[] = [];

  constructor(
    private readonly fb: FormBuilder,
    private readonly clientCvTemplateService: ClientCvTemplateService
  ) {}

  ngOnInit(): void {
    const defaultSalutation = this.locale === 'en' ? 'Dear Sir or Madam,' : 'Geachte heer, mevrouw,';
    this.form = this.fb.group({
      pasted_vacancy_text: new FormControl<string | null>(null),
      availability:        new FormControl<string | null>(this.availabilityValue || null),
      claim_strength:      new FormControl<ClaimStrength>(this.claimStrength ?? 'soft', Validators.required),
      salutation:          new FormControl<string>(this.salutationValue || defaultSalutation),
      template_id:         new FormControl<string>(this.templateId ?? this.templates[0]?.id ?? 'editorial', Validators.required),
      template_type:       new FormControl<string>(this.templateType || 'built_in'),
      client_cv_template_id: new FormControl<number | null>(this.clientCvTemplateId ?? null),
      locale:              new FormControl<string>(this.locale ?? 'nl', Validators.required),
    });
    this.loadBrandedTemplates();
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
    this.subs = [];
  }

  public get canSubmit(): boolean {
    const base = !this.submitting
      && !this.busy
      && !this.disabled
      && this.form.get('claim_strength')?.valid === true
      && this.form.get('template_id')?.valid === true
      && this.form.get('locale')?.valid === true;
    return base;
  }

  public onSubmit(): void {
    if (!this.canSubmit) return;

    const fv = this.form.value;
    this.regenerate.emit({
      template_id: fv.template_id,
      locale: fv.locale,
      template_type: fv.template_type || 'built_in',
      client_cv_template_id: fv.client_cv_template_id ?? null,
      claim_strength: fv.claim_strength,
      availability: fv.availability?.trim() || undefined,
      salutation: fv.salutation?.trim() || undefined,
    });
  }

  public onCancel(): void {
    this.cancelled.emit();
  }

  public get selectedPickerValue(): string | null {
    if (!this.form) return null;
    const fv = this.form.value;
    if (fv.template_type === 'client' && fv.client_cv_template_id) {
      return `client:${fv.client_cv_template_id}`;
    }
    return fv.template_id ? `built_in:${fv.template_id}` : null;
  }

  public onTemplatePickerChange(value: string | null): void {
    if (!this.form || !value) return;
    const [kind, rawId] = value.split(':');

    let next: { template_id: string; template_type: string; client_cv_template_id: number | null };
    if (kind === 'client') {
      const id = Number(rawId);
      const tpl = this.brandedTemplates.find(t => t.id === id);
      if (!tpl) return;
      next = { template_id: tpl.base_layout_id, template_type: 'client', client_cv_template_id: tpl.id };
    } else {
      next = { template_id: rawId, template_type: 'built_in', client_cv_template_id: null };
    }

    this.form.patchValue(next);
    // Settings mode: apply the template instantly (no agent re-run, #723). In
    // create mode the template is applied when the package is created, so we
    // only keep the form in sync here.
    if (this.mode === 'settings') {
      this.templateChanged.emit(next);
    }
  }

  private loadBrandedTemplates(): void {
    if (!this.clientId) {
      this.brandedTemplates = [];
      return;
    }

    this.brandedLoading = true;
    const sub = this.clientCvTemplateService.list(this.clientId).subscribe({
      next: page => {
        this.brandedLoading = false;
        this.brandedTemplates = (page.data || []).filter(t => t.status === 'ready' && t.is_active);
      },
      error: err => {
        this.brandedLoading = false;
        this.brandedTemplates = [];
        console.error('tailor-intake: load branded templates failed', err);
      }
    });
    this.subs.push(sub);
  }
}
